import React, { useRef } from "react";
import { Compass, MousePointer2, PenTool, RotateCw, Trash2, Undo2, Upload, FileDown, Layers, ImageIcon, MapPin, Magnet, Hash } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

export default function Toolbar({
  mode, setMode,
  onUpload, onLoadSample,
  onUndo, onClear,
  hasImage, pointCount,
  showOverlay, setShowOverlay,
  showDegrees, setShowDegrees,
  snapEnabled, setSnapEnabled, cornerCount = 0,
}) {
  const fileRef = useRef(null);

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex flex-col gap-1.5 p-2 bg-slate-900 border border-slate-800 rounded-xl shadow-xl">
        <input
          ref={fileRef}
          type="file"
          accept=".jpg,.jpeg,.png,.svg,.pdf,image/jpeg,image/png,image/svg+xml,application/pdf"
          className="hidden"
          data-testid="floorplan-upload-input"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onUpload(f);
            e.target.value = "";
          }}
        />

        <ToolBtn label="Upload floor plan" testid="upload-btn" onClick={() => fileRef.current?.click()}>
          <Upload className="h-4 w-4" />
        </ToolBtn>

        <ToolBtn label="Load sample plan" testid="load-sample-plan-button" onClick={onLoadSample}>
          <ImageIcon className="h-4 w-4" />
        </ToolBtn>

        <div className="h-px bg-slate-800 my-1" />

        <ToolBtn
          label="Draw polygon corners"
          testid="mode-draw-btn"
          active={mode === "draw"}
          onClick={() => setMode("draw")}
          disabled={!hasImage}
        >
          <PenTool className="h-4 w-4" />
        </ToolBtn>

        <ToolBtn
          label="Pan / Select (hold shift)"
          testid="mode-pan-btn"
          active={mode === "pan"}
          onClick={() => setMode("pan")}
          disabled={!hasImage}
        >
          <MousePointer2 className="h-4 w-4" />
        </ToolBtn>

        <ToolBtn
          label="Place room markers"
          testid="mode-marker-btn"
          active={mode === "marker"}
          onClick={() => setMode("marker")}
          disabled={!hasImage}
        >
          <MapPin className="h-4 w-4" />
        </ToolBtn>

        <div className="h-px bg-slate-800 my-1" />

        <ToolBtn label="Undo last point" testid="undo-point-button" onClick={onUndo} disabled={pointCount === 0}>
          <Undo2 className="h-4 w-4" />
        </ToolBtn>

        <ToolBtn label="Clear polygon" testid="clear-polygon-button" onClick={onClear} disabled={pointCount === 0}>
          <Trash2 className="h-4 w-4" />
        </ToolBtn>

        <div className="h-px bg-slate-800 my-1" />

        <ToolBtn
          label={showOverlay ? "Hide overlay" : "Show overlay"}
          testid="toggle-overlay-btn"
          active={showOverlay}
          onClick={() => setShowOverlay(!showOverlay)}
        >
          <Layers className="h-4 w-4" />
        </ToolBtn>

        <ToolBtn
          label={showDegrees ? "Hide degree labels" : "Show degree labels"}
          testid="toggle-degrees-btn"
          active={showDegrees}
          onClick={() => setShowDegrees(!showDegrees)}
        >
          <Hash className="h-4 w-4" />
        </ToolBtn>

        <ToolBtn
          label={snapEnabled ? `Snap to corners: on (${cornerCount} detected)` : "Snap to corners: off"}
          testid="snap-toggle-btn"
          active={snapEnabled}
          onClick={() => setSnapEnabled(!snapEnabled)}
          disabled={!hasImage}
        >
          <Magnet className="h-4 w-4" />
        </ToolBtn>
      </div>
    </TooltipProvider>
  );
}

function ToolBtn({ label, children, active, onClick, disabled, testid }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          data-testid={testid}
          onClick={onClick}
          disabled={disabled}
          className={`h-9 w-9 flex items-center justify-center rounded-lg transition-colors
            ${active ? "bg-amber-500 text-slate-950 hover:bg-amber-400" : "text-slate-300 hover:bg-slate-800"}
            disabled:opacity-40 disabled:cursor-not-allowed`}
        >
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent side="right" className="bg-slate-950 border border-slate-800 text-slate-200 font-mono text-[11px]">
        {label}
      </TooltipContent>
    </Tooltip>
  );
}
